import { useState, useRef, useEffect } from 'react'
import type { HTMLAttributes, TableHTMLAttributes, ReactNode, MouseEvent } from 'react'
import { Button } from './Button'
import { exportToXLSX, exportToPDF, copyToClipboard, exportToCSV, exportArraysToXLSX, exportArraysToPDF, copyArraysToClipboard, exportArraysToCSV } from '../../utils/export'
import type { ExportColumn } from '../../utils/export'
import { IconExcel, IconPrinter, IconCopy, IconPDF, IconFileText } from '../Icons'
import { AlertModal } from './AlertModal'

export interface ExportConfig<T> {
  data: T[]
  columns: ExportColumn<T>[]
  filename: string
}

export interface TableWrapperProps<T = any> extends HTMLAttributes<HTMLDivElement> {
  exportConfig?: ExportConfig<T>
  exportFilename?: string
  searchPlaceholder?: string
  searchValue?: string
  onSearchChange?: (val: string) => void
  showSearch?: boolean
}

export function TableWrapper<T = any>({
  children,
  className = '',
  exportConfig,
  exportFilename,
  standalone = false,
  searchPlaceholder,
  searchValue,
  onSearchChange,
  showSearch,
  ...props
}: TableWrapperProps<T> & { standalone?: boolean }) {
  const [isExporting, setIsExporting] = useState(false)
  const [isExportingCSV, setIsExportingCSV] = useState(false)
  const [isPrinting, setIsPrinting] = useState(false)
  const [isCopying, setIsCopying] = useState(false)
  const [isExportingPDF, setIsExportingPDF] = useState(false)
  const [alertState, setAlertState] = useState<{ isOpen: boolean, title: string, message: string, type: 'success' | 'error' } | null>(null)

  const wrapperRef = useRef<HTMLDivElement>(null)

  const [localSearch, setLocalSearch] = useState('')
  const displaySearch = showSearch !== false && (onSearchChange !== undefined || standalone)
  const currentSearchValue = onSearchChange !== undefined ? (searchValue || '') : localSearch
  const handleSearchChange = onSearchChange !== undefined ? onSearchChange : setLocalSearch

  useEffect(() => {
    if (onSearchChange !== undefined) return
    if (!wrapperRef.current) return

    const table = wrapperRef.current.querySelector('table')
    if (!table) return

    const rows = Array.from(table.querySelectorAll('tbody tr'))
    const term = localSearch.toLowerCase().trim()

    rows.forEach(row => {
      // Ignore skeleton or empty state children
      if (row.querySelector('.ui-table-skeleton__row')) return
      if (row.querySelector('.ui-empty-state')) return

      const text = row.textContent?.toLowerCase() || ''
      if (!term || text.includes(term)) {
        (row as HTMLElement).style.display = ''
      } else {
        (row as HTMLElement).style.display = 'none'
      }
    })
  }, [localSearch, children, onSearchChange])

  const getTableData = () => {
    if (!wrapperRef.current) return { headers: [], rows: [] }
    const table = wrapperRef.current.querySelector('table')
    if (!table) return { headers: [], rows: [] }

    const ths = Array.from(table.querySelectorAll('thead th'))
    const allHeaders = ths.map(th => th.textContent?.trim() || '')
    const validIndices = allHeaders.map((h, i) => h && h.toLowerCase() !== 'ações' ? i : -1).filter(i => i !== -1)
    
    const headers = validIndices.map(i => allHeaders[i])

    const rows = Array.from(table.querySelectorAll('tbody tr')).map(tr => {
      const tds = Array.from(tr.querySelectorAll('td'))
      return validIndices.map(i => tds[i]?.textContent?.trim() || '')
    })

    return { headers, rows }
  }

  const handlePrintAction = async () => {
    setIsPrinting(true)
    try {
      await new Promise(resolve => setTimeout(resolve, 300))
      window.print()
    } finally {
      setIsPrinting(false)
    }
  }

  const handleExcelAction = async () => {
    if (!exportConfig && !exportFilename) return
    setIsExporting(true)
    try {
      await new Promise(resolve => setTimeout(resolve, 500))
      if (exportConfig) {
        exportToXLSX(exportConfig.data, exportConfig.columns, exportConfig.filename)
      } else if (exportFilename) {
        const { headers, rows } = getTableData()
        exportArraysToXLSX(headers, rows, exportFilename)
      }
    } finally {
      setIsExporting(false)
    }
  }

  const handleCSVAction = async () => {
    if (!exportConfig && !exportFilename) return
    setIsExportingCSV(true)
    try {
      await new Promise(resolve => setTimeout(resolve, 500))
      if (exportConfig) {
        exportToCSV(exportConfig.data, exportConfig.columns, exportConfig.filename)
      } else if (exportFilename) {
        const { headers, rows } = getTableData()
        exportArraysToCSV(headers, rows, exportFilename)
      }
    } finally {
      setIsExportingCSV(false)
    }
  }

  const handleCopyAction = async () => {
    if (!exportConfig && !exportFilename) return
    setIsCopying(true)
    try {
      await new Promise(resolve => setTimeout(resolve, 300))
      
      let success = false
      if (exportConfig) {
        success = await copyToClipboard(exportConfig.data, exportConfig.columns)
      } else {
        const { headers, rows } = getTableData()
        success = await copyArraysToClipboard(headers, rows)
      }

      if (success) {
        setAlertState({
          isOpen: true,
          title: 'Copiado!',
          message: 'Os dados da tabela foram copiados para sua área de transferência.',
          type: 'success'
        })
      }
    } catch (err) {
      setAlertState({
        isOpen: true,
        title: 'Erro ao Copiar',
        message: 'Não foi possível copiar os dados. Verifique as permissões do navegador.',
        type: 'error'
      })
    } finally {
      setIsCopying(false)
    }
  }

  const handlePDFAction = async () => {
    if (!exportConfig && !exportFilename) return
    setIsExportingPDF(true)
    try {
      await new Promise(resolve => setTimeout(resolve, 500))
      if (exportConfig) {
        exportToPDF(exportConfig.data, exportConfig.columns, exportConfig.filename)
      } else if (exportFilename) {
        const { headers, rows } = getTableData()
        exportArraysToPDF(headers, rows, exportFilename)
      }
    } finally {
      setIsExportingPDF(false)
    }
  }

  if (!exportConfig && !exportFilename && !displaySearch) {
    return (
      <div 
        className={`ui-table-wrapper ${className}`.trim()} 
        {...props}
        style={standalone ? {
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: '16px',
          boxShadow: 'var(--shadow-xs)',
          overflowX: 'auto',
          overflowY: 'hidden'
        } : undefined}
      >
        {children}
      </div>
    )
  }

  return (
    <div 
      ref={wrapperRef}
      className={`ui-table-container ${className}`.trim()} 
      {...props} 
      style={{ 
        position: 'relative', 
        ...(standalone ? {
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: '16px',
          borderTopRightRadius: '0',
          borderTopLeftRadius: displaySearch ? '0' : '16px',
          boxShadow: 'var(--shadow-xs)',
          overflow: 'visible',
          marginTop: '56px' // Space for the floating tab
        } : {})
      }}
    >
      <style>{`
        .ui-table-container .ui-table th:last-child {
          padding-right: 16px !important;
        }
      `}</style>
      
      {displaySearch && (
        <div style={{
          position: 'absolute',
          bottom: standalone ? '100%' : 'auto',
          top: standalone ? 'auto' : '5px',
          left: standalone ? '-1px' : '12px',
          marginBottom: standalone ? '-1px' : '0',
          background: standalone ? 'var(--surface-2)' : 'transparent',
          borderTop: standalone ? '1px solid var(--border)' : 'none',
          borderLeft: standalone ? '1px solid var(--border)' : 'none',
          borderRight: standalone ? '1px solid var(--border)' : 'none',
          borderBottom: standalone ? '1px solid var(--surface-2)' : 'none',
          padding: standalone ? '8px 12px' : '0',
          borderRadius: standalone ? '16px 12px 0 0' : '12px',
          display: 'flex',
          alignItems: 'center',
          zIndex: 10
        }}>
          <input
            type="text"
            placeholder={searchPlaceholder || "Pesquisar..."}
            value={currentSearchValue}
            onChange={e => handleSearchChange(e.target.value)}
            style={{
              height: '30px',
              padding: '0 12px',
              fontSize: '13px',
              border: '1px solid var(--border)',
              borderRadius: '8px',
              background: 'var(--surface)',
              width: '280px',
              outline: 'none',
              color: 'var(--text-1)'
            }}
          />
        </div>
      )}

      {(exportConfig || exportFilename) && (
        <div style={{ 
          position: 'absolute', 
          bottom: standalone ? '100%' : 'auto', 
          top: standalone ? 'auto' : '5px',
          right: standalone ? '-1px' : '12px', 
          marginBottom: standalone ? '-1px' : '0', 
          background: standalone ? 'var(--surface-2)' : 'transparent', 
          borderTop: standalone ? '1px solid var(--border)' : 'none',
          borderLeft: standalone ? '1px solid var(--border)' : 'none',
          borderRight: standalone ? '1px solid var(--border)' : 'none',
          borderBottom: standalone ? '1px solid var(--surface-2)' : 'none',
          padding: standalone ? '8px 12px' : '0', 
          borderRadius: standalone ? '12px 16px 0 0' : '12px', 
          display: 'flex', 
          gap: '8px',
          zIndex: 10 
        }}>
          <Button variant="secondary" title="Copiar" onClick={handleCopyAction} disabled={isCopying} style={{ width: '28px', height: '28px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {isCopying ? (
              <svg className="ui-spinner" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px', animation: 'spin 1s linear infinite' }}>
                <path d="M21 12a9 9 0 1 1-6.219-8.56"></path>
              </svg>
            ) : <IconCopy />}
          </Button>

          <Button variant="secondary" title="Exportar para Excel (XLSX)" onClick={handleExcelAction} disabled={isExporting} style={{ width: '28px', height: '28px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {isExporting ? (
              <svg className="ui-spinner" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px', animation: 'spin 1s linear infinite' }}>
                <path d="M21 12a9 9 0 1 1-6.219-8.56"></path>
              </svg>
            ) : <IconExcel />}
          </Button>
          
          <Button variant="secondary" title="Exportar para CSV" onClick={handleCSVAction} disabled={isExportingCSV} style={{ width: '28px', height: '28px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {isExportingCSV ? (
              <svg className="ui-spinner" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px', animation: 'spin 1s linear infinite' }}>
                <path d="M21 12a9 9 0 1 1-6.219-8.56"></path>
              </svg>
            ) : <IconFileText />}
          </Button>

          <Button variant="secondary" title="Exportar para PDF" onClick={handlePDFAction} disabled={isExportingPDF} style={{ width: '28px', height: '28px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {isExportingPDF ? (
              <svg className="ui-spinner" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px', animation: 'spin 1s linear infinite' }}>
                <path d="M21 12a9 9 0 1 1-6.219-8.56"></path>
              </svg>
            ) : <IconPDF />}
          </Button>

          <Button variant="secondary" title="Imprimir" onClick={handlePrintAction} disabled={isPrinting} style={{ width: '28px', height: '28px', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {isPrinting ? (
              <svg className="ui-spinner" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px', animation: 'spin 1s linear infinite' }}>
                <path d="M21 12a9 9 0 1 1-6.219-8.56"></path>
              </svg>
            ) : <IconPrinter />}
          </Button>
        </div>
      )}
      
      <div 
        className="ui-table-wrapper" 
        style={standalone ? { 
          border: 'none',
          background: 'transparent',
          boxShadow: 'none',
          borderTopLeftRadius: displaySearch ? '0' : '16px', 
          borderBottomLeftRadius: '16px', 
          borderBottomRightRadius: '16px', 
          borderTopRightRadius: '0', 
          overflowX: 'auto',
          overflowY: 'hidden' 
        } : {}}
      >
        {children}
      </div>

      {alertState && alertState.isOpen && (
        <AlertModal
          title={alertState.title}
          message={alertState.message}
          type={alertState.type}
          onClose={() => setAlertState(null)}
        />
      )}
    </div>
  )
}

export function Table({ children, className = '', ...props }: TableHTMLAttributes<HTMLTableElement>) {
  return (
    <table className={`ui-table ${className}`.trim()} {...props}>
      {children}
    </table>
  )
}

export function TableSkeleton({ rows = 5, className = '' }: { rows?: number, className?: string }) {
  return (
    <div className={`ui-table-skeleton ${className}`.trim()}>
      {[...Array(rows)].map((_, i) => (
        <div key={i} className="ui-table-skeleton__row" style={{ width: `${90 - i * 4}%` }} />
      ))}
    </div>
  )
}

export function EmptyState({ icon, message, subMessage, action, className = '' }: { icon?: ReactNode, message: string, subMessage?: string, action?: ReactNode, className?: string }) {
  return (
    <div className={`ui-empty-state ${className}`.trim()}>
      {icon || <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>}
      <p style={{ fontWeight: 500 }}>{message}</p>
      {subMessage && <p style={{ fontSize: '13px', color: 'var(--text-4)' }}>{subMessage}</p>}
      {action && <div style={{ marginTop: '0.5rem' }}>{action}</div>}
    </div>
  )
}

interface PaginationProps {
  currentPage: number
  totalPages: number
  totalItems: number
  currentItemsLength: number
  onPageChange: (page: number) => void
}

export function Pagination({ currentPage, totalPages, totalItems, currentItemsLength, onPageChange }: PaginationProps) {
  if (totalPages <= 1) return null;

  const handlePageChange = (page: number) => {
    onPageChange(page)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <div className="ui-pagination">
      <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
        Mostrando {currentItemsLength} de {totalItems} itens
      </span>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <Button
          variant="secondary"
          disabled={currentPage === 1}
          onClick={() => handlePageChange(currentPage - 1)}
        >
          Anterior
        </Button>
        <span style={{ display: 'flex', alignItems: 'center', margin: '0 0.5rem', fontSize: '0.9rem' }}>
          Página {currentPage} de {totalPages}
        </span>
        <Button
          variant="secondary"
          disabled={currentPage === totalPages}
          onClick={() => handlePageChange(currentPage + 1)}
        >
          Próxima
        </Button>
      </div>
    </div>
  )
}

export function Badge({ children, variant = 'primary', className = '', ...props }: HTMLAttributes<HTMLSpanElement> & { variant?: 'primary' | 'success' | 'warning' | 'danger' | 'info' | 'default' }) {
  return (
    <span className={`ui-badge ui-badge--${variant} ${className}`.trim()} {...props}>
      {children}
    </span>
  )
}

export function ActionButton({
  icon,
  title,
  onClick,
  disabled,
  danger = false,
  'aria-label': ariaLabel,
  ...rest
}: {
  icon: ReactNode
  title: string
  onClick: (e: MouseEvent<HTMLButtonElement>) => void
  disabled?: boolean
  danger?: boolean
  'aria-label'?: string
  [key: string]: any
}) {
  const baseClass = 'ui-action-btn-icon'
  const dangerClass = danger ? 'ui-action-btn-icon--danger' : ''
  return (
    <button
      type="button"
      className={`${baseClass} ${dangerClass}`.trim()}
      title={title}
      aria-label={ariaLabel || title}
      onClick={(e) => onClick(e)}
      disabled={disabled}
      {...rest}
    >
      {icon}
    </button>
  )
}

Table.Wrapper = TableWrapper
Table.Skeleton = TableSkeleton
Table.EmptyState = EmptyState
Table.Pagination = Pagination
Table.Badge = Badge
Table.ActionButton = ActionButton
